"use client";
/**
 * Language switcher for themes (flags + code). Renders nothing with a single
 * language. Uses the /<locale> prefix, or the configured locale domain when
 * multi-domain mode is on (passed through SiteShell block context).
 */
import { useEffect, useRef, useState } from "react";
import { useBlock } from "./blocks/context";
import { localeSwitchUrl } from "./i18n";
import { LocaleFlag } from "./LocaleFlag";

declare global {
  interface Window {
    __CMS_I18N?: { domains?: Record<string, string>; alternates?: Record<string, string> };
  }
}

export function LangSwitcher({ className }: { className?: string }) {
  const ctx = useBlock();
  const [open, setOpen] = useState(false);
  const alternates = ctx.localeAlternates;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (ctx.locales.length < 2) return null;

  const switchTo = (target: string) => {
    if (ctx.editing) return; // builder canvas: never navigate
    localStorage.setItem("cms-lang-chosen", "1");
    const url = localeSwitchUrl(target, ctx.locale, ctx.locales, location, ctx.localeDomains, alternates);
    if (url) location.href = url;
  };

  const shownLocales = alternates ? ctx.locales.filter((locale) => locale === ctx.locale || alternates[locale]) : ctx.locales;
  if (shownLocales.length < 2) return null;

  return (
    <div ref={ref} className={`lang-switcher ${className || ""}`}>
      <button type="button" className="lang-current" onClick={() => setOpen(!open)} aria-expanded={open}>
        <LocaleFlag locale={ctx.locale} /><span>{ctx.locale.toUpperCase()}</span><span aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className="lang-menu">
          {shownLocales.map((l) => (
            <li key={l}>
              <button
                type="button"
                className={l === ctx.locale ? "is-active" : ""}
                onClick={() => {
                  setOpen(false);
                  if (l !== ctx.locale) switchTo(l);
                }}
              >
                <LocaleFlag locale={l} /><span>{l.toUpperCase()}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * First-visit browser-language redirect: when the visitor's browser language
 * matches another enabled locale, go there once. Respects explicit choices
 * made through the switcher (localStorage flag).
 */
export function LangAutoRedirect({ locale, locales, domains, alternates }: {
  locale: string;
  locales: string[];
  domains?: Record<string, string>;
  alternates?: Record<string, string>;
}) {
  useEffect(() => {
    if (locales.length < 2) return;
    if (localStorage.getItem("cms-lang-chosen")) return;
    localStorage.setItem("cms-lang-chosen", "1"); // only ever redirect once
    const wanted = (navigator.language || "").slice(0, 2).toLowerCase();
    if (!wanted || wanted === locale || !locales.includes(wanted)) return;
    const url = localeSwitchUrl(wanted, locale, locales, location, domains, alternates);
    if (url) location.replace(url);
  }, [locale, locales, domains, alternates]);
  return null;
}
